import type { ActionContext } from './types'
import {
  extractActionTextValue,
  hasNormalizedActionTransfer,
} from './normalizeActionTransfer'

export const IS_CONTEXT_VALUES_FOR_PROCEDURE_LOOP = [
  'call',
  'context&doc',
  'context&explicit',
  'call&context',
  'call&explicit',
  'call&return',
  'call&doc',
] as const

/** Дублирует useActions.needToExecuteProcedure. */
export const TRANSFER_TYPES_REQUIRING_PROCEDURE = [
  'context_call_and_return',
  'context_call_conditional_change_doc',
  'call_chain',
  'call_chain_conditional_change_doc',
  'call_chain_and_return',
  'call_chain_conditional_return',
  'call_chain_and_explicit_view',
] as const

export function resolveIsContextActionValue(context: ActionContext): string {
  return String(extractActionTextValue(context.data?.isContext) ?? '').trim()
}

/** Маршрутные action с document parameter (getParameter / setDocument). */
export function isDocumentContextAction(context: ActionContext): boolean {
  const value = resolveIsContextActionValue(context)
  return value === 'context&doc' || value === 'call&doc'
}

function transferTypeRequiresProcedure(transferType: unknown): boolean {
  const normalized = String(extractActionTextValue(transferType) ?? '').trim().toLowerCase()
  return (TRANSFER_TYPES_REQUIRING_PROCEDURE as readonly string[]).includes(normalized)
}

/**
 * Transfer types whose doCommonAction also executeChain (Swing/platform).
 * With isContext none/empty Swing skips execureProcedure and runs chain only in doAction —
 * webui must not also enter the procedure loop (double insert).
 */
const TRANSFER_TYPES_CHAIN_IN_DO_ACTION_ONLY_WHEN_NO_IS_CONTEXT = [
  'call_chain_and_return',
] as const

/**
 * Swing `ActionCall.performPreAction`: для XML `isContext=call` всегда `return false`
 * (нет createDialog/createWindow и нет window.doAction).
 */
export function shouldContinueAfterPerformPreAction(context: ActionContext): boolean {
  return resolveIsContextActionValue(context) !== 'call'
}

/**
 * true, если для контекста будет входить цикл procedure + chainCalled (как в performPreAction).
 */
export function shouldExecuteProcedureLoop(context: ActionContext): boolean {
  const isContextAction = resolveIsContextActionValue(context)
  const values = IS_CONTEXT_VALUES_FOR_PROCEDURE_LOOP as readonly string[]
  if (isContextAction && values.includes(isContextAction)) {
    return true
  }
  if (!hasNormalizedActionTransfer(context.transfer, context.transferType)) {
    return false
  }
  const normalizedIsContext = isContextAction.trim().toLowerCase()
  if (!normalizedIsContext || normalizedIsContext === 'none') {
    const normalizedTransferType = String(
      extractActionTextValue(context.transferType) ?? '',
    )
      .trim()
      .toLowerCase()
    if (
      (TRANSFER_TYPES_CHAIN_IN_DO_ACTION_ONLY_WHEN_NO_IS_CONTEXT as readonly string[]).includes(
        normalizedTransferType,
      )
    ) {
      return false
    }
  }
  return transferTypeRequiresProcedure(context.transferType)
}
